<template>
  <div class="page">
    <div class="public-wrap auth-box">
      <p class="step-label">Family · {{ step }} / 6</p>
      <h1>{{ titles[step - 1] }}</h1>
      <p class="lead">{{ leads[step - 1] }}</p>
      <div id="lilo-recaptcha"></div>

      <v-alert v-if="done" type="success" class="mb-4">Your family account is ready. You can add more on this page or open the Lilo app with this phone.</v-alert>
      <v-alert v-if="error" type="error" class="mb-4">{{ error }}</v-alert>

      <v-form v-if="!done && step === 1" ref="phoneForm" @submit.prevent="sendCode">
        <div class="phone-row">
          <span>+251</span>
          <v-text-field v-model="phoneLocal" label="Mobile" outlined dense hide-details :rules="[required]" placeholder="911100001"></v-text-field>
        </div>
        <v-btn color="primary" class="mt-6" type="submit" :loading="busy" depressed block>Send SMS code</v-btn>
      </v-form>

      <div v-else-if="!done && step === 2">
        <otp-boxes v-model="otp" @complete="checkCode" />
      </div>

      <v-form v-else-if="!done && step === 3" ref="youForm" @submit.prevent="saveYou">
        <v-select v-model="prefix" :items="prefixes" label="Prefix" outlined dense :rules="[required]"></v-select>
        <v-text-field v-model="firstName" label="First name" outlined dense :rules="[required]"></v-text-field>
        <v-text-field v-model="middleName" label="Middle name" outlined dense :rules="[required]"></v-text-field>
        <v-text-field v-model="lastName" label="Last name" outlined dense :rules="[required]"></v-text-field>
        <v-text-field v-model="birthDate" label="Birthday" type="date" outlined dense :rules="[required]"></v-text-field>
        <v-text-field v-model="email" label="Email (optional)" outlined dense></v-text-field>
        <v-text-field v-model="password" label="App password" type="password" outlined dense :rules="[required, min]" hint="Used on the Lilo family app" persistent-hint></v-text-field>
        <v-text-field v-model="confirmPassword" label="Confirm password" type="password" outlined dense :rules="[required, min, match]"></v-text-field>
        <region-city-fields
          :region-id.sync="regionId"
          :city-id.sync="cityId"
          :area.sync="area"
          required
          @center="mapCenter = $event"
        />
        <v-btn color="primary" class="mt-6" type="submit" :loading="busy" depressed block>Continue</v-btn>
      </v-form>

      <div v-else-if="!done && step === 4">
        <p class="hint">A clear face photo helps the school and driver know you.</p>
        <input type="file" accept="image/*" @change="onPhoto($event, 'parent')" />
        <img v-if="parentPreview" :src="parentPreview" class="preview" alt="" />
        <v-btn color="primary" class="mt-6" :loading="busy" depressed block @click="savePhoto">{{ parentPreview ? 'Save photo' : 'Skip for now' }}</v-btn>
      </div>

      <div v-else-if="!done && step === 5">
        <div v-for="(g, i) in guardians" :key="i" class="subcard">
          <v-text-field v-model="g.name" label="Guardian name" outlined dense></v-text-field>
          <v-text-field v-model="g.phone" label="Guardian phone" outlined dense></v-text-field>
          <v-text-field v-model="g.email" label="Guardian email (optional)" outlined dense></v-text-field>
          <input type="file" accept="image/*" @change="onPhoto($event, 'guardian', i)" />
        </div>
        <v-btn text color="primary" @click="guardians.push({ name: '', phone: '', email: '', photo: null })">Add another guardian</v-btn>
        <v-btn color="primary" class="mt-4" :loading="busy" depressed block @click="saveGuardians">Continue</v-btn>
      </div>

      <div v-else-if="!done && step === 6">
        <div v-for="(c, i) in children" :key="i" class="subcard">
          <v-text-field v-model="c.name" label="Child name" outlined dense></v-text-field>
          <v-text-field v-model="c.ident" label="Student ID / class" outlined dense></v-text-field>
          <v-text-field v-model="c.schoolCode" label="School code" outlined dense hint="Six-digit code from the school" persistent-hint></v-text-field>
          <input type="file" accept="image/*" @change="onPhoto($event, 'child', i)" />
          <p class="hint mt-4">Pickup on the map</p>
          <osm-autocomplete :id="'pickup-' + i" placeholder="Search pickup address" @place_changed="place => setChildPlace(i, place)" />
          <google-map-loader
            :center="mapCenter"
            :zoom="13"
            :enabled="true"
            :markers="c.lat ? [{ position: { lat: () => c.lat, lng: () => c.lng }, title: c.name || 'Pickup' }] : []"
            class="map"
            @map-click="place => setChildPlace(i, place)"
          />
        </div>
        <v-btn text color="primary" @click="children.push(emptyChild())">Add another child</v-btn>
        <v-btn color="primary" class="mt-4" :loading="busy" depressed block @click="saveChildren">Finish</v-btn>
      </div>

      <p class="mt-6">
        <router-link to="/signin">Sign in</router-link>
        <span class="mx-2">·</span>
        <router-link to="/register">Back</router-link>
      </p>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import PhoneAuth from '@/services/PhoneAuthService'
import OtpBoxes from '@/components/OtpBoxes.vue'
import RegionCityFields from '@/components/RegionCityFields.vue'
import GoogleMapLoader from '@/components/GoogleMapLoader.vue'
import OsmAutocomplete from '@/components/OsmAutocomplete.vue'
import { Keys } from '/src/config.js'

export default {
  components: { GoogleMapLoader, OsmAutocomplete, OtpBoxes, RegionCityFields },
  data() {
    return {
      step: 1,
      busy: false,
      done: false,
      error: '',
      session: null,
      phoneLocal: '',
      otp: '',
      prefix: 'Mr',
      prefixes: ['Mr', 'Mrs', 'Ms', 'Dr'],
      firstName: '',
      middleName: '',
      lastName: '',
      birthDate: '',
      email: '',
      password: '',
      confirmPassword: '',
      regionId: null,
      cityId: null,
      area: '',
      parentPhoto: null,
      parentPreview: '',
      guardians: [{ name: '', phone: '', email: '', photo: null }],
      children: [this.emptyChild()],
      mapCenter: { lat: Number(Keys.VUE_APP_ORIGIN_LAT), lng: Number(Keys.VUE_APP_ORIGIN_LNG) },
      titles: ['Your phone', 'SMS code', 'Your details', 'Your photo', 'Other guardians', 'Children'],
      leads: [
        'Family registration starts with a mobile number. We send a one-time SMS.',
        'Enter the 6-digit SMS code. Verification starts when the last digit is in.',
        'Email is optional. Birthday is used for your family record.',
        'Optional. The school can recognize you at pickup.',
        'Add other adults who may collect the children. Skip if it is only you.',
        'Add each child, their school code, and the pickup pin.',
      ],
    }
  },
  computed: {
    phone() {
      return PhoneAuth.e164(this.phoneLocal)
    },
    fullName() {
      return [this.prefix, this.firstName, this.middleName, this.lastName].filter(Boolean).join(' ')
    },
    required() {
      return (v) => !!v || 'Required'
    },
    min() {
      return (v) => !v || v.length >= 8 || 'At least 8 characters'
    },
    match() {
      return (v) => v === this.password || 'Passwords do not match'
    },
  },
  methods: {
    emptyChild() {
      return { name: '', ident: '', schoolCode: '', pic: null, lat: null, lng: null, address: '' }
    },
    async sendCode() {
      this.error = ''
      this.busy = true
      try {
        this.session = await PhoneAuth.sendOtp(this.phone, 'lilo-recaptcha')
        this.otp = ''
        this.step = 2
      } catch (e) {
        this.error = (e.response && e.response.data && (e.response.data.message || e.response.data.errors)) || e.message
      } finally {
        this.busy = false
      }
    },
    async checkCode(code) {
      const otp = String(code || this.otp || '')
      if (this.busy || otp.length !== 6) return
      this.error = ''
      this.busy = true
      try {
        await PhoneAuth.confirmOtp(this.session, otp)
        this.step = 3
      } catch (e) {
        this.error = (e.response && e.response.data && e.response.data.errors && e.response.data.errors.otp && e.response.data.errors.otp[0]) || e.message
      } finally {
        this.busy = false
      }
    },
    async saveYou() {
      if (this.$refs.youForm && !this.$refs.youForm.validate()) return
      this.error = ''
      this.busy = true
      try {
        const res = await axios.post('/auth/createParent', {
          name: this.fullName,
          token: 'phone',
          device_name: 'Lilo-web',
          password: this.password,
          phone: PhoneAuth.digits(this.phone),
          email: this.email || undefined,
          prefix: this.prefix,
          first_name: this.firstName,
          middle_name: this.middleName,
          last_name: this.lastName,
          birth_date: this.birthDate,
          region_id: this.regionId,
          city_id: this.cityId,
          area: this.area,
        })
        PhoneAuth.storeToken(res.data.token)
        await axios.post('/users/update-profile', {
          tel_number: PhoneAuth.digits(this.phone),
          prefix: this.prefix,
          first_name: this.firstName,
          middle_name: this.middleName,
          last_name: this.lastName,
          birth_date: this.birthDate,
          name: this.fullName,
          region_id: this.regionId,
          city_id: this.cityId,
          area: this.area,
        })
        this.step = 4
      } catch (e) {
        this.error = (e.response && e.response.data && (e.response.data.message || (e.response.data.errors && (e.response.data.errors.authentication || Object.values(e.response.data.errors)[0])))) || e.message || 'Could not create the family account'
        if (Array.isArray(this.error)) this.error = this.error[0]
      } finally {
        this.busy = false
      }
    },
    onPhoto(event, kind, index) {
      const file = event.target.files && event.target.files[0]
      if (!file) return
      const reader = new FileReader()
      reader.onload = () => {
        if (kind === 'parent') {
          this.parentPhoto = file
          this.parentPreview = reader.result
        } else if (kind === 'guardian') {
          this.guardians[index].photo = file
        } else {
          this.children[index].pic = reader.result
        }
      }
      reader.readAsDataURL(file)
    },
    async savePhoto() {
      this.busy = true
      try {
        if (this.parentPhoto) {
          const data = new FormData()
          data.append('avatar', this.parentPhoto)
          await axios.post('/users/upload-avatar', data)
        }
        this.step = 5
      } catch (e) {
        this.error = e.message
      } finally {
        this.busy = false
      }
    },
    async saveGuardians() {
      this.busy = true
      this.error = ''
      try {
        for (const g of this.guardians) {
          if (!g.name || (!g.phone && !g.email)) continue
          await axios.post('/users/add-guardian', {
            name: g.name,
            phone: g.phone,
            email: g.email || undefined,
          })
        }
        this.step = 6
      } catch (e) {
        this.error = (e.response && e.response.data && e.response.data.message) || e.message
      } finally {
        this.busy = false
      }
    },
    setChildPlace(i, place) {
      const lat = place.geometry.location.lat()
      const lng = place.geometry.location.lng()
      this.children[i].lat = lat
      this.children[i].lng = lng
      this.children[i].address = place.formatted_address
      this.mapCenter = { lat, lng }
    },
    async saveChildren() {
      this.busy = true
      this.error = ''
      try {
        for (const c of this.children) {
          if (!c.name) continue
          let schoolId = null
          if (c.schoolCode) {
            const school = await axios.get(`/users/get-school-by-code/${c.schoolCode}`)
            schoolId = school.data.school && school.data.school.id
          }
          if (!schoolId) {
            this.error = `Add the school code for ${c.name}`
            this.busy = false
            return
          }
          const created = await axios.post('/users/add-edit-student', {
            name: c.name,
            student_identification: c.ident || c.name,
            school_id: schoolId,
            notes: 'Registered on ridelilo.com',
            pic: c.pic || undefined,
          })
          const studentId = created.data.student && created.data.student.id
          if (studentId && c.lat) {
            await axios.post('/stops/set-pickup-drop-off-location', {
              student_id: studentId,
              lat: c.lat,
              lng: c.lng,
              address: c.address,
              pick_up: 1,
            })
          }
        }
        this.done = true
      } catch (e) {
        this.error = (e.response && e.response.data && e.response.data.message) || e.message
      } finally {
        this.busy = false
      }
    },
  },
}
</script>
<style scoped>
.page { padding: 48px 0 80px; font-family: var(--font); }
.public-wrap { width: min(640px, calc(100% - 32px)); margin: 0 auto; }
.auth-box {
  background: var(--white);
  color: var(--navy);
  border-radius: 24px;
  padding: 32px;
  box-shadow: var(--shadow);
}
h1 { color: var(--navy); margin: 0 0 8px; }
.lead, .hint, .step-label { color: var(--slate); }
.step-label { text-transform: uppercase; letter-spacing: .08em; font-size: 12px; font-weight: 700; }
.phone-row { display: flex; gap: 10px; align-items: center; }
.phone-row span { background: var(--canvas); border: 1px solid var(--line); border-radius: 12px; padding: 12px 14px; font-weight: 700; }
.subcard { border: 1px solid var(--line); border-radius: 16px; padding: 16px; margin: 0 0 14px; }
.preview { width: 120px; height: 120px; object-fit: cover; border-radius: 16px; margin-top: 12px; }
.map { height: 240px; margin-top: 10px; border-radius: 16px; overflow: hidden; }
</style>
